import { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import StatCard from '../../components/admin/StatCard';
import { Users, FileText, Image, DollarSign, CreditCard, TrendingUp } from 'lucide-react';
import axios from 'axios';
import './AdminStyles.css';

// Используем относительный путь для админ панели
const API_URL = '/api';

function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      const response = await axios.get(`${API_URL}/admin/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      setError('Не удалось загрузить статистику');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout currentPage="dashboard">
        <div className="admin-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <div className="admin-loader"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout currentPage="dashboard">
        <div className="admin-page" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <p style={{ color: '#ef4444', fontSize: '18px', marginBottom: '16px' }}>{error}</p>
          <button onClick={fetchStats} className="admin-button admin-button-primary">
            Повторить
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="dashboard">
      <div className="admin-page">
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '32px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>Дашборд</h1>
          <p style={{ color: '#6b7280' }}>Общая статистика платформы</p>
        </div>

        {/* User Stats */}
        <div className="stats-grid">
          <StatCard
            title="Всего пользователей"
            value={stats?.users?.total || 0}
            subtitle={`Новых за неделю: ${stats?.users?.newThisWeek || 0}`}
            icon={Users}
            color="primary"
          />
          <StatCard
            title="Premium пользователей"
            value={stats?.users?.premium || 0}
            subtitle={`Free: ${stats?.users?.free || 0}`}
            icon={TrendingUp}
            color="success"
          />
          <StatCard
            title="Всего документов"
            value={stats?.documents?.total || 0}
            subtitle={`За месяц: ${stats?.documents?.thisMonth || 0}`}
            icon={FileText}
            color="purple"
          />
          <StatCard
            title="Всего печатей"
            value={stats?.stamps?.total || 0}
            subtitle={`Печати: ${stats?.stamps?.stamps || 0}, Подписи: ${stats?.stamps?.signatures || 0}`}
            icon={Image}
            color="warning"
          />
        </div>

        {/* Revenue & Payments */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', marginTop: '24px' }}>
          <div className="admin-card">
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CreditCard size={20} style={{ color: '#3b82f6' }} />
              Подписки
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ color: '#6b7280', fontWeight: '500' }}>Всего подписок:</span>
                <span style={{ fontSize: '20px', fontWeight: '700', color: '#111827' }}>
                  {stats?.subscriptions?.total || 0}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                <span style={{ color: '#6b7280' }}>Активных:</span>
                <span className="badge badge-success">
                  {stats?.subscriptions?.active || 0}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                <span style={{ color: '#6b7280' }}>Истекло:</span>
                <span className="badge badge-danger">
                  {stats?.subscriptions?.expired || 0}
                </span>
              </div>
              <div style={{ paddingTop: '16px', marginTop: '16px', borderTop: '2px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#6b7280', fontWeight: '600' }}>Общая выручка:</span>
                  <span style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>
                    {(stats?.subscriptions?.revenue || 0).toLocaleString()} сум
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="admin-card">
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <DollarSign size={20} style={{ color: '#10b981' }} />
              Платежи
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ color: '#6b7280', fontWeight: '500' }}>Всего транзакций:</span>
                <span style={{ fontSize: '20px', fontWeight: '700', color: '#111827' }}>
                  {stats?.payments?.totalTransactions || 0}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                <span style={{ color: '#6b7280' }}>Завершенных:</span>
                <span className="badge badge-success">
                  {stats?.payments?.completed || 0}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                <span style={{ color: '#6b7280' }}>В обработке:</span>
                <span className="badge badge-warning">
                  {stats?.payments?.pending || 0}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                <span style={{ color: '#6b7280' }}>Ошибка:</span>
                <span className="badge badge-danger">
                  {stats?.payments?.failed || 0}
                </span>
              </div>
              <div style={{ paddingTop: '16px', marginTop: '16px', borderTop: '2px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#6b7280', fontWeight: '600' }}>Общий доход:</span>
                  <span style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>
                    {(stats?.payments?.totalRevenue || 0).toLocaleString()} сум
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminDashboardPage;

